import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Pagination } from '@core/models/pagination.model';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { Store } from '@ngxs/store';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { IFILTER } from 'app/app.constants';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { BehaviorSubject, Observable, Subscription, take } from 'rxjs';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';

@Component({
  selector: 'app-facial-recognition-log',
  standalone: true,
  imports: [
    CommonModule,
    FiltersComponent,
    TableSkeletonLoaderComponent,
    TableNavigatorComponent,
    PermissionDirective,
    TableRefreshComponent,
  ],
  templateUrl: './facial-recognition-log.component.html',
  styleUrl: './facial-recognition-log.component.scss',
})
export class FacialRecognitionLogComponent implements OnInit, OnDestroy {
  facialRecognitionLogList$ = new BehaviorSubject<any>(null).asObservable();
  isFetchingList$!: Observable<boolean>;
  totalCount$!: Observable<number>;
  subs: Subscription[] = [];
  isFilterActive = false;
  hadFilterBefore = false;

  filterStructure: IFILTER[] = [
    {
      filterKey: 'status',
      label: 'Status',
      type: 'dropdown',
      dropdown: [
        { key: 'PASSED', value: 'Passed' },
        { key: 'FAILED', value: 'Failed' },
      ],
    },
    {
      filterKey: 'function_type',
      label: 'Function Type',
      type: 'dropdown',
      dropdown: [
        { key: 'DETECTION', value: 'Detection' },
        { key: 'RECOGNITION', value: 'Recognition' },
      ],
    },
    {
      filterKey: 'start_date',
      label: 'Start Date',
      type: 'date',
    },
    {
      filterKey: 'end_date',
      label: 'End Date',
      type: 'date',
    },
  ];

  store = inject(Store);
  breadCrumbService = inject(BreadCrumbService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);

  constructor() {
    this.facialRecognitionLogList$ = this.store.select(
      TableSelectors.getTableData('facialRecognitionLog')
    );
    this.totalCount$ = this.store.select(
      TableSelectors.getTableTotalCount('facialRecognitionLog')
    );
    this.isFetchingList$ = this.store.select(
      TableSelectors.getTableLoading('facialRecognitionLog')
    );
  }

  ngOnInit() {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);
    this.filterEventListener();
    this.loadList();
  }

  loadList(page = 1) {
    const sub = this.facialRecognitionLogList$.pipe(take(1)).subscribe((list) => {
      const hasData = Array.isArray(list) && list.length > 0;
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'facialRecognitionLog',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  filterEventListener() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';

      if (params['status']) {
        query += `status=${params['status']}&`;
      }
      if (params['function_type']) {
        query += `function_type=${params['function_type']}&`;
      }
      if (params['start_date']) {
        query += `start_date=${params['start_date']}&`;
      }
      if (params['end_date']) {
        query += `end_date=${params['end_date']}&`;
      }
      if (params['search']) {
        query += `user_id=${params['search']}&`;
      }

      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.isFilterActive = true;
        this.hadFilterBefore = true;
        this.store.dispatch(
          new TableActions.FilterFacialRecognitionLogList({ filter: query })
        );
      } else {
        this.isFilterActive = false;
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'facialRecognitionLog',
              params: new Pagination({ skip: skip, size: take }),
            })
          );
        }
      }
    });
  }


  getName(user: any) {
    if (!user) return '-';
    return `${user.first_name || ''} ${user.last_name || ''} ${user.middle_name || ''}`.trim() || '-';
  }

  navigate(currentPage: number) {
    this.router.navigate([], {
      queryParams: { page: currentPage },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
